import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {assert} from 'assert-ts';
import type {WorkSize} from 'services/settings/types';
import {selectWorkSize, workSizeUpdated} from 'services/settings';
import {useAppDispatch, useAppSelector} from 'store/hooks';

type WorkSizeContextType = {
  workSize: WorkSize | undefined;
  setWorkSize: (workSize: WorkSize | undefined) => void;
};

export const WorkSizeContext = createContext<WorkSizeContextType | undefined>(
  undefined,
);

export const useWorkSizeContextProviderValue = (): WorkSizeContextType => {
  const dispatch = useAppDispatch();
  const persistedWorkSize = useAppSelector(selectWorkSize);
  const [workSize, setLocalWorkSize] = useState(persistedWorkSize);

  const setWorkSize = useCallback(
    (workSize: WorkSize | undefined) => {
      dispatch(workSizeUpdated(workSize));
      startTransition(() => setLocalWorkSize(workSize));
    },
    [dispatch],
  );

  return useMemo(
    () => ({
      workSize,
      setWorkSize,
    }),
    [setWorkSize, workSize],
  );
};

export const useWorkSizeContext = (): WorkSizeContextType => {
  return assert(
    useContext(WorkSizeContext),
    'WorkSizeContext: context expected',
  );
};
